/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";

// PrimeNG imports
import { ConfirmationService, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

// Our components and services
import {
  QPilotAccessToken,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import {
  DEFAULT_PAYMENT_INTEGRATION_FILTER,
  PaymentIntegration,
  PaymentIntegrationFilterParams,
  PaymentIntegrationListResponse,
} from "../../models/payment-integration.model";
import { PaymentIntegrationsService } from "../../services/payment-integrations.service";
import {
  PaymentIntegrationsFilterControls,
  PaymentIntegrationsFiltersComponent,
} from "../../components/payment-integrations-filters/payment-integrations-filters.component";
import {
  PaymentIntegrationsTableComponent,
  PaymentIntegrationRowAction,
  SortEvent,
  PageEvent,
} from "../../components/payment-integrations-table/payment-integrations-table.component";
import { ListPageSkeletonComponent } from "../../../../shared/components/table-skeleton";

@Component({
  selector: "app-payment-integrations-list",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    ConfirmDialogModule,
    ToastModule,
    TooltipModule,
    PaymentIntegrationsFiltersComponent,
    PaymentIntegrationsTableComponent,
    ListPageSkeletonComponent,
  ],
  templateUrl: "./payment-integrations-list.component.html",
  styleUrl: "./payment-integrations-list.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
  providers: [ConfirmationService],
})
export class PaymentIntegrationsListComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sessionService = inject(SessionService);
  private readonly elementRef = inject(ElementRef);
  private readonly paymentIntegrationsService = inject(
    PaymentIntegrationsService,
  );
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  private readonly destroy$ = new Subject<void>();

  // Web component / embedded mode inputs
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() embeddedMode: boolean = false;

  // Reactive state
  readonly siteId = signal<number | null>(null);
  readonly sidebarCollapsed = signal(false);
  private userManuallyCollapsed = false;

  // Data signals
  private readonly rawData = signal<PaymentIntegrationListResponse | null>(
    null,
  );
  private readonly filteredItems = signal<PaymentIntegration[]>([]);

  // Loading states
  readonly isLoading = signal(false);
  readonly isInitialLoading = signal(true);

  // Form
  filterForm!: FormGroup<PaymentIntegrationsFilterControls>;

  // Form values signal for reactive computed
  private readonly formValues = signal<
    Partial<{
      clientFilterId: number | null;
      clientFilterName: string | null;
      clientFilterProvider: string | null;
      clientFilterTestMode: boolean | null;
    }>
  >({});

  // Check if any client-side filters are active (must be defined before data)
  readonly hasActiveFilters = computed(() => {
    const values = this.formValues();
    return !!(
      values.clientFilterId !== null ||
      values.clientFilterName?.trim() ||
      values.clientFilterProvider ||
      values.clientFilterTestMode !== null
    );
  });

  // Computed values
  readonly data = computed(() => {
    const raw = this.rawData();
    const filtered = this.filteredItems();
    const hasFilters = this.hasActiveFilters();

    if (!raw) return null;

    // When client-side filters are active, use filtered count
    // Otherwise, use the API's total count for proper pagination
    return {
      ...raw,
      Items: filtered,
      TotalCount: hasFilters ? filtered.length : raw.TotalCount,
    } as PaymentIntegrationListResponse;
  });

  readonly loading = computed(() => this.isLoading());
  readonly initialLoading = computed(() => this.isInitialLoading());

  constructor() {
    // Initialize form
    this.filterForm = this.fb.group<PaymentIntegrationsFilterControls>({
      clientFilterId: this.fb.control(null),
      clientFilterName: this.fb.control(null),
      clientFilterProvider: this.fb.control(null),
      clientFilterTestMode: this.fb.control(null),
      page: this.fb.nonNullable.control(
        DEFAULT_PAYMENT_INTEGRATION_FILTER.page!,
      ),
      pageSize: this.fb.nonNullable.control(
        DEFAULT_PAYMENT_INTEGRATION_FILTER.pageSize!,
      ),
      orderBy: this.fb.control(DEFAULT_PAYMENT_INTEGRATION_FILTER.orderBy!),
      order: this.fb.nonNullable.control(
        DEFAULT_PAYMENT_INTEGRATION_FILTER.order!,
      ),
    });
  }

  ngOnInit(): void {
    // Detect embedded mode from HTML attribute (critical for web components)
    const element = this.elementRef.nativeElement as HTMLElement;
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");

    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Initialize based on mode
    if (this.embeddedMode) {
      this.initializeEmbeddedMode();
    } else {
      this.initializeSiteId();
    }

    this.initializeResponsiveSidebar();
    this.setupWindowResizeListener();
    this.setupDataSubscriptions();
  }

  /**
   * Initialize for embedded/web component mode
   */
  private initializeEmbeddedMode(): void {
    const element = this.elementRef.nativeElement as HTMLElement;

    // Get siteId from input/attribute
    const siteIdString =
      this.inputSiteId?.toString() ??
      element.getAttribute("input-site-id") ??
      element.getAttribute("inputSiteId");

    if (siteIdString) {
      this.siteId.set(parseInt(siteIdString, 10));
    }

    // Get token from attribute and convert to QPilotAccessToken
    const tokenString =
      element.getAttribute("input-token") ?? element.getAttribute("inputToken");

    if (
      tokenString &&
      (!this.inputToken ||
        typeof this.inputToken !== "object" ||
        !this.inputToken.TokenBearerAuth)
    ) {
      this.inputToken = {
        TokenBearerAuth: tokenString,
        TokenAuth: "",
        Id: 0,
        UserId: 0,
        CreatedUtc: new Date(),
        UpdatedUtc: new Date(),
        ExpiresUtc: new Date(),
        User: {} as QPilotUser,
        FirstLogin: 0,
      };
    }

    // Set the token in session service so HTTP interceptor can use it
    if (this.inputToken) {
      this.sessionService.setAccessToken(this.inputToken);
    }

    this.cdr.markForCheck();
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeSiteId(): void {
    this.route.parent?.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const siteIdParam = params.get("siteId");
        if (siteIdParam) {
          const siteId = parseInt(siteIdParam, 10);
          this.siteId.set(siteId);
          this.cdr.markForCheck();
        }
      });
  }

  private setupDataSubscriptions(): void {
    let isFirstLoad = true;

    // Update formValues signal for hasActiveFilters computed (client-side filter tracking)
    this.filterForm.valueChanges
      .pipe(startWith(this.filterForm.value), takeUntil(this.destroy$))
      .subscribe((formValue) => {
        this.formValues.set({
          clientFilterId: formValue.clientFilterId,
          clientFilterName: formValue.clientFilterName,
          clientFilterProvider: formValue.clientFilterProvider,
          clientFilterTestMode: formValue.clientFilterTestMode,
        });
        // Apply client-side filters whenever filter values change (no API call)
        this.applyClientSideFilters();
        this.cdr.markForCheck();
      });

    // API subscription - ONLY for pagination/sorting changes
    // Client-side filters do NOT trigger API calls
    this.filterForm.valueChanges
      .pipe(
        startWith(this.filterForm.value),
        // Extract only the API-relevant fields
        map(
          (
            formValue,
          ): {
            page: number;
            pageSize: number;
            orderBy: string;
            order: "asc" | "desc";
          } => {
            return {
              page: formValue.page || 1,
              pageSize: formValue.pageSize || 25,
              orderBy: formValue.orderBy || "UpdatedUtc",
              order: formValue.order || "desc",
            };
          },
        ),
        // Only continue if pagination/sorting actually changed
        distinctUntilChanged(
          (
            prev: {
              page: number;
              pageSize: number;
              orderBy: string;
              order: "asc" | "desc";
            },
            curr: {
              page: number;
              pageSize: number;
              orderBy: string;
              order: "asc" | "desc";
            },
          ) =>
            prev.page === curr.page &&
            prev.pageSize === curr.pageSize &&
            prev.orderBy === curr.orderBy &&
            prev.order === curr.order,
        ),
        debounceTime(300),
        switchMap((apiParams) => {
          const siteId = this.siteId();
          if (!siteId) return of(null);

          // Set loading states
          if (isFirstLoad) {
            this.isInitialLoading.set(true);
          }
          this.isLoading.set(true);

          // Build filter params for API (only pagination/sorting)
          const apiFilters: Partial<PaymentIntegrationFilterParams> = {
            siteId,
            page: apiParams.page,
            pageSize: apiParams.pageSize,
            orderBy: apiParams.orderBy,
            order: apiParams.order,
          };

          return this.paymentIntegrationsService
            .getPaymentIntegrationsForSite(siteId, apiFilters)
            .pipe(
              catchError((error) => {
                console.error("Error loading payment integrations:", error);
                this.messageService.add({
                  severity: "error",
                  summary: "Error",
                  detail: "Failed to load payment integrations",
                  life: 5000,
                });
                return of(null);
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this.rawData.set(result);
            // Apply client-side filters to the new data
            this.applyClientSideFilters();
          }

          this.isLoading.set(false);
          if (isFirstLoad) {
            this.isInitialLoading.set(false);
            isFirstLoad = false;
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error("Subscription error:", error);
          this.isLoading.set(false);
          if (isFirstLoad) {
            this.isInitialLoading.set(false);
            isFirstLoad = false;
          }
          this.cdr.markForCheck();
        },
      });
  }

  private applyClientSideFilters(): void {
    const raw = this.rawData();
    if (!raw) {
      this.filteredItems.set([]);
      return;
    }

    const formValue = this.filterForm.value;
    const filters: Partial<PaymentIntegrationFilterParams> = {
      clientFilterId: formValue.clientFilterId,
      clientFilterName: formValue.clientFilterName,
      clientFilterProvider: formValue.clientFilterProvider,
      clientFilterTestMode: formValue.clientFilterTestMode,
    };

    const filtered = this.paymentIntegrationsService.applyClientSideFilters(
      raw.Items,
      filters,
    );
    this.filteredItems.set(filtered);
  }

  onFiltersChanged(): void {
    // Reset to first page when filters change
    this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
    this.applyClientSideFilters();
    this.cdr.markForCheck();
  }

  onClearFilters(): void {
    this.filterForm.patchValue({
      clientFilterId: null,
      clientFilterName: null,
      clientFilterProvider: null,
      clientFilterTestMode: null,
      page: 1,
    });
  }

  onSortChanged(event: SortEvent): void {
    this.filterForm.patchValue({
      orderBy: event.field,
      order: event.order === 1 ? "asc" : "desc",
      page: 1,
    });
  }

  onPageChanged(event: PageEvent): void {
    const currentPage =
      event.page !== undefined
        ? event.page
        : Math.floor(event.first / event.rows);

    this.filterForm.patchValue({
      page: currentPage + 1,
      pageSize: event.rows,
    });
  }

  onRowAction(event: {
    action: PaymentIntegrationRowAction;
    item: PaymentIntegration;
  }): void {
    switch (event.action) {
      case "edit":
        this.editPaymentIntegration(event.item);
        break;
      case "delete":
        this.deletePaymentIntegration(event.item);
        break;
    }
  }

  private editPaymentIntegration(item: PaymentIntegration): void {
    const siteId = this.siteId();
    if (!siteId) return;

    if (this.embeddedMode) {
      // Emit navigation event for web component/iframe mode
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "payment-integrations-list",
          action: "edit",
          id: item.Id,
          siteId: siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      // Standard app mode - use Angular Router
      void this.router.navigate([
        "/widgets",
        "dashboard",
        siteId,
        "payment-integrations",
        item.Id,
        "edit",
      ]);
    }
  }

  private deletePaymentIntegration(item: PaymentIntegration): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete this payment integration (${item.PaymentMethodType})?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptIcon: "pi pi-trash",
      acceptButtonStyleClass: "p-button-danger",
      acceptLabel: "Yes, delete",
      rejectLabel: "Cancel",
      accept: () => {
        this.performDelete(item);
      },
    });
  }

  private performDelete(item: PaymentIntegration): void {
    const siteId = this.siteId();
    if (!siteId) return;

    this.paymentIntegrationsService
      .deletePaymentIntegration(siteId, item.Id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Payment integration deleted successfully",
          });
          this.refreshData();
        },
        error: (error) => {
          console.error("Delete failed:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to delete payment integration",
            life: 5000,
          });
        },
      });
  }

  private refreshData(): void {
    const siteId = this.siteId();
    if (!siteId) return;

    this.isLoading.set(true);

    const formValue = this.filterForm.value;
    const apiFilters: Partial<PaymentIntegrationFilterParams> = {
      siteId,
      page: formValue.page || 1,
      pageSize: formValue.pageSize || 25,
      orderBy: formValue.orderBy || "UpdatedUtc",
      order: formValue.order || "desc",
    };

    this.paymentIntegrationsService
      .getPaymentIntegrationsForSite(siteId, apiFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.rawData.set(result);
          this.applyClientSideFilters();
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error("Error refreshing data:", error);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  onCreateNew(): void {
    const siteId = this.siteId();
    if (!siteId) return;

    if (this.embeddedMode) {
      // Emit navigation event for web component/iframe mode
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "payment-integrations-list",
          action: "create",
          siteId: siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      // Standard app mode - use Angular Router
      void this.router.navigate([
        "/widgets",
        "dashboard",
        siteId,
        "payment-integrations",
        "create",
      ]);
    }
  }

  toggleSidebar(): void {
    this.userManuallyCollapsed = true;
    this.sidebarCollapsed.update((v) => !v);
  }

  private initializeResponsiveSidebar(): void {
    const isMobile = window.innerWidth <= 768;
    this.sidebarCollapsed.set(isMobile);
  }

  private setupWindowResizeListener(): void {
    const resizeObserver = new ResizeObserver(() => {
      const isMobile = window.innerWidth <= 768;

      if (isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(true);
      } else if (!isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(false);
      }

      this.cdr.markForCheck();
    });

    resizeObserver.observe(document.body);

    this.destroy$.subscribe(() => {
      resizeObserver.disconnect();
    });
  }
}
